import { useState } from 'react';
import Button from '@watershed/ui-core/components/Button';
import { GQDiscussionProductSection } from '@watershed/shared-universal/generated/graphql';
import { Box, ListItemIcon, Menu, MenuItem, useTheme } from '@mui/material';
import { useCommentDiscussionsForAnchorQuery } from '@watershed/shared-frontend/generated/urql';
import { isFetchingOrDataBang } from '@watershed/shared-frontend/utils/isFetchingOrStale';
import { DiscussionsDialog } from './DiscussionsDialog';
import { isComplete } from '@watershed/shared-universal/utils/discussionUtils';
import orderBy from 'lodash/orderBy';
import { getLatestTimestampForDataIssue } from '@watershed/shared-frontend/utils/dataIssueUtils';
import { getSharedSidebarStyles } from '../navigation/variants/sharedSidebarStyles';
import usePaletteUtils from '@watershed/ui-core/hooks/usePaletteUtils';
import { OpenDiscussionCount } from './OpenDiscussionCount';
import { routeForLearningHub } from '@watershed/shared-universal/dashboardRoutes';
import UnstyledLink from '@watershed/ui-core/components/UnstyledLink';
import HelpIcon from '@watershed/icons/components/Help';
import LightBulbIcon from '@watershed/icons/components/LightBulb';
import ConversationIcon from '@watershed/icons/components/Conversation';
import { Trans } from '@lingui/macro';

export function HelpNavButton({
  isOnFinancePage,
}: {
  isOnFinancePage: boolean;
}) {
  const productSection = isOnFinancePage
    ? GQDiscussionProductSection.Finance
    : GQDiscussionProductSection.Global;
  const anchor = { productSection };
  const [isDiscussionDialogOpen, setIsDiscussionDialogOpen] =
    useState<boolean>(false);
  const [result] = useCommentDiscussionsForAnchorQuery({
    variables: {
      input: anchor,
    },
  });
  const { fetching, data } = isFetchingOrDataBang(result);
  const discussions = data?.commentDiscussionsForAnchor.discussions ?? [];
  const openDiscussionCount = discussions.filter(
    (discussion) => !isComplete(discussion.state)
  ).length;

  const orderedDiscussions = orderBy(
    discussions,
    [getLatestTimestampForDataIssue],
    ['desc']
  );
  const [helpAnchorEl, setHelpAnchorEl] = useState<null | HTMLButtonElement>(
    null
  );

  const { COLORS } = getSharedSidebarStyles(useTheme());
  const paletteUtils = usePaletteUtils();

  return (
    <>
      <Button
        variant="text"
        startIcon={<HelpIcon />}
        aria-controls="help-menu"
        aria-haspopup="true"
        onClick={(e) => setHelpAnchorEl(e.currentTarget)}
        sx={{
          width: '100%',
          height: '48px',
          justifyContent: 'flex-start',
          textAlign: 'left',
          color: COLORS.TEXT_PRIMARY,
          borderLeft: '4px solid transparent',
          borderRadius: 0,
          paddingLeft: '14px', // Match alignment of gear icon
          paddingRight: '18px',

          '&:hover, &:focus': {
            backgroundColor: COLORS.BACKGROUND_ACTIVE,
          },
          '&:focus': {
            boxShadow: paletteUtils.boxShadowFocus,
          },
          '&[aria-current="page"]': {
            color: COLORS.TEXT_PRIMARY,
            backgroundColor: COLORS.BACKGROUND_ACTIVE,
            borderColor: COLORS.BORDER_ACTIVE,
          },
          '& .MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: 0,
            '& svg': {
              width: 20,
              height: 20,
            },
          },
        }}
        aria-current={
          isDiscussionDialogOpen || helpAnchorEl ? 'page' : undefined
        }
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingY: 2,
            marginLeft: '8px', // Match alignment of navbar items
          }}
        >
          <Trans>Help</Trans>
        </Box>
      </Button>
      <Menu
        id="help-menu"
        anchorEl={helpAnchorEl}
        keepMounted
        open={Boolean(helpAnchorEl)}
        onClose={() => setHelpAnchorEl(null)}
        anchorOrigin={{
          vertical: -4,
          horizontal: 'right',
        }}
      >
        <UnstyledLink
          href={routeForLearningHub({
            from: 'Sidebar',
            viewMode: isOnFinancePage ? 'finance' : 'regular',
          })}
        >
          <MenuItem
            onClick={() => {
              setHelpAnchorEl(null);
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 36,
              }}
            >
              <LightBulbIcon />
            </ListItemIcon>
            <Trans>Learning hub</Trans>
          </MenuItem>
        </UnstyledLink>
        <MenuItem
          disabled={fetching}
          onClick={() => {
            setHelpAnchorEl(null);
            setIsDiscussionDialogOpen(true);
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
            }}
          >
            <ConversationIcon />
          </ListItemIcon>
          <OpenDiscussionCount
            count={openDiscussionCount}
            label={<Trans>Support</Trans>}
          />
        </MenuItem>
      </Menu>
      <DiscussionsDialog
        discussions={orderedDiscussions}
        anchor={{
          productSection,
        }}
        openDiscussionCount={openDiscussionCount}
        isDiscussionDialogOpen={isDiscussionDialogOpen}
        setIsDiscussionDialogOpen={setIsDiscussionDialogOpen}
      />
    </>
  );
}
